import { FC, useMemo, useContext } from "react";

import { StepLogLine } from "./StepLogLine";
import { IStepLog } from "./stepLog.types";
import { Translate, GetLanguageValueAsString } from "@/app/application/Translate";
import CloseIcon from "@mui/icons-material/Close";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import {
	Box,
	IconButton,
	Stack,
	Typography,
	alpha,
	useTheme,
} from "@mui/material";

import {
	useFetchProcedureStepLog,
	useFetchSuggestionsByChecklistId,
} from "@/service/ProcedureQueries";
import { Language } from "@/model/Language/Language";

import { TranslationContext } from "@/app/providers";


export const StepLog: FC<IStepLog> = (props) => {
	const translation: Language | null = useContext(TranslationContext);
	const { toggleShowStepLog, showStepLog, activeSchemeId, step } = props;
	const theme = useTheme();

	const { data: log } = useFetchProcedureStepLog(
		showStepLog,
		activeSchemeId,
		step?.Id,
	);
	const { data: suggestionLog } =
		useFetchSuggestionsByChecklistId(activeSchemeId);

	if (!showStepLog) return null;

	const entries = log?.Entries ?? [];
	const stepSuggestions = useMemo(
		() =>
			Array.isArray(suggestionLog)
				? suggestionLog.filter((s) => s.StepId === step?.Id)
				: [],
		[suggestionLog, step?.Id],
	);

	const hasAnything = entries.length > 0 || stepSuggestions.length > 0;
	const edgeColor = alpha(
		theme.palette.mode === "light"
			? theme.palette.common.black
			: theme.palette.common.white,
		0.04,
	);
	const dividerColor = alpha(theme.palette.divider, 0.35);

	return (
		<Box
			sx={{
				mt: 2,
				mb: 1,
				borderRadius: 2,
				overflow: "hidden",
				bgcolor: "background.paper",
				boxShadow: `inset 0 0 0 1px ${edgeColor}`,
				transition: "box-shadow .18s ease",
			}}
		>
			{/* Header: same bg as step, very light separation */}
			<Stack
				direction="row"
				alignItems="center"
				justifyContent="space-between"
				sx={{
					px: 2,
					py: 1.25,
					borderBottom: `1px solid ${dividerColor}`,
					gap: 1.5,
				}}
			>
				<Stack
					direction="row"
					alignItems="center"
					spacing={1.5}
					sx={{ minWidth: 0 }}
				>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							width: 32,
							height: 32,
							borderRadius: 1.25,
							bgcolor: alpha(theme.palette.info.main, 0.06),
							color: "info.main",
						}}
					>
						<HistoryOutlinedIcon sx={{ fontSize: 18 }} />
					</Box>
					<Box sx={{ minWidth: 0 }}>
						<Typography
							variant="subtitle2"
							sx={{
								fontWeight: 600,
								lineHeight: 1.3,
								color: "text.primary",
							}}
						>
							<Translate String="Procedure.Checklist.Step.Log.Heading">
								{hasAnything ? "Activity log" : "No activity"}
							</Translate>
						</Typography>
						<Typography
							variant="caption"
							sx={{
								display: "block",
								mt: 0.25,
								lineHeight: 1.3,
								color: "text.secondary",
								opacity: 0.85,
							}}
						>
							{entries.length} {entries.length === 1 ? "entry" : "entries"}
							{stepSuggestions.length > 0 && (
								<>
									{" · "}
									{stepSuggestions.length} improvement{" "}
									{stepSuggestions.length === 1 ? "suggestion" : "suggestions"}
								</>
							)}
						</Typography>
					</Box>
				</Stack>

				<IconButton
					aria-label={GetLanguageValueAsString(translation, "Auto.Features.Procedures.Ui.Components.Checklists.Checklist.Step.StepLog.L134C17.CloseLog", "Close log")}
					onClick={toggleShowStepLog}
					size="small"
					sx={{
						flexShrink: 0,
						borderRadius: 1.5,
						color: "text.secondary",
						"&:hover": { bgcolor: "action.hover", color: "text.primary" },
					}}
				>
					<CloseIcon sx={{ fontSize: 20 }} />
				</IconButton>
			</Stack>

			{/* Body */}
			<Box sx={{ px: 2, py: 1.5 }}>
				{!hasAnything && (
					<Typography
						variant="body2"
						color="text.secondary"
						sx={{ py: 2, textAlign: "center" }}
					>
						<Translate String="Procedure.Checklist.Step.Log.Empty">
							There's no activity for this step yet.
						</Translate>
					</Typography>
				)}

				{entries.length > 0 && (
					<Stack spacing={0} sx={{ "& > * + *": { mt: 1.5 } }}>
						{entries.map((entry) => (
							<StepLogLine key={entry.Id} logLine={entry} />
						))}
					</Stack>
				)}
			</Box>
		</Box>
	);
};
